import React from 'react'
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react'
import { ChevronDownIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import type { PortalOnlyAccountVO } from '../../../apis/types'
import { formatReadableDate, formatCurrency } from '../../../util/string'

interface PortalOnlyAccountsTableProps {
    accounts: PortalOnlyAccountVO[]
}

const hasVnemAllocation = (acct: PortalOnlyAccountVO): boolean =>
    acct.vnemPct != null && acct.vnemPct > 0

const PortalOnlyAccountsTable: React.FC<PortalOnlyAccountsTableProps> = ({ accounts }) => {
    if (accounts.length === 0) return null
    const anyVnemFlagged = accounts.some(hasVnemAllocation)

    return (
        <div className="mt-4">
            <Disclosure defaultOpen={true}>
                {({ open }) => (
                    <>
                        <DisclosureButton className="flex w-full items-center justify-between rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 px-4 py-3 text-left text-sm font-medium text-amber-900 dark:text-amber-200 hover:bg-amber-100 dark:hover:bg-amber-900/30 focus:outline-none">
                            <span>
                                Portal-Only Accounts{' '}
                                <span className="inline-flex items-center ml-2 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-200 dark:bg-amber-800 text-amber-800 dark:text-amber-200">
                                    {accounts.length}
                                </span>
                            </span>
                            <ChevronDownIcon className={`w-5 h-5 text-amber-500 transition-transform ${open ? 'rotate-180' : ''}`} />
                        </DisclosureButton>
                        <DisclosurePanel className="border border-t-0 border-amber-200 dark:border-amber-800 rounded-b-lg">
                            <div className="px-4 py-3 bg-amber-50 dark:bg-amber-900/10 border-b border-amber-200 dark:border-amber-800">
                                <p className="text-xs text-amber-700 dark:text-amber-300">
                                    These accounts exist on the utility portal but have no matching account in OneBill.
                                    {anyVnemFlagged && ' Rows flagged with a red border are receiving a VNEM allocation despite not being in OneBill — investigate before billing.'}
                                </p>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="min-w-full text-sm">
                                    <thead>
                                        <tr className="bg-slate-50 dark:bg-slate-800 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                                            <th className="px-4 py-2">Account Number</th>
                                            <th className="px-4 py-2">Account Name</th>
                                            <th className="px-4 py-2">Active</th>
                                            <th className="px-4 py-2">VNEM %</th>
                                            <th className="px-4 py-2">Service Address</th>
                                            <th className="px-4 py-2">Last Scraper Run</th>
                                            <th className="px-4 py-2">Last Stmt Date</th>
                                            <th className="px-4 py-2">Last Stmt Amount</th>
                                            <th className="px-4 py-2">Last Payment Date</th>
                                            <th className="px-4 py-2">Last Payment Amount</th>
                                            <th className="px-4 py-2">Issue</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {accounts.map((acct, i) => {
                                            const hasVnem = hasVnemAllocation(acct)
                                            const rowClass = hasVnem
                                                ? 'bg-red-50 dark:bg-red-900/20'
                                                : i % 2 === 0
                                                    ? 'bg-white dark:bg-slate-900'
                                                    : 'bg-slate-50 dark:bg-slate-800/50'
                                            const firstCellClass = `px-4 py-2 font-mono text-slate-900 dark:text-slate-100${
                                                hasVnem ? ' border-l-4 border-red-500 dark:border-red-400' : ''
                                            }`
                                            return (
                                                <tr
                                                    key={acct.utilityAccountNumber}
                                                    className={rowClass}
                                                    aria-label={hasVnem ? 'Portal-only account flagged: receiving VNEM allocation but not in OneBill' : undefined}
                                                >
                                                    <td className={firstCellClass}>{acct.utilityAccountNumber}</td>
                                                    <td className="px-4 py-2 text-slate-900 dark:text-slate-100">{acct.accountName || '—'}</td>
                                                    <td className="px-4 py-2">
                                                        {acct.active == null ? (
                                                            <span className="text-slate-400">—</span>
                                                        ) : acct.active ? (
                                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-400/10 text-green-700 dark:text-green-400 ring-1 ring-inset ring-green-600/10 dark:ring-green-400/20">Active</span>
                                                        ) : (
                                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 dark:bg-slate-700/50 text-slate-500 dark:text-slate-400 ring-1 ring-inset ring-slate-300 dark:ring-slate-600">Inactive</span>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-2">
                                                        {hasVnem ? (
                                                            <span
                                                                title="This portal-only account is receiving a VNEM allocation despite not being in OneBill"
                                                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-200 ring-1 ring-inset ring-red-600/30 dark:ring-red-400/30"
                                                            >
                                                                <ExclamationTriangleIcon className="w-3 h-3" aria-hidden="true" />
                                                                {acct.vnemPct?.toFixed(2)}%
                                                            </span>
                                                        ) : (
                                                            <span className="text-slate-400">—</span>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-2 text-slate-700 dark:text-slate-300">{acct.serviceAddress || '—'}</td>
                                                    <td className="px-4 py-2 text-slate-700 dark:text-slate-300">{formatReadableDate(acct.lastScraperRun) || '—'}</td>
                                                    <td className="px-4 py-2 text-slate-700 dark:text-slate-300">{formatReadableDate(acct.lastStmtDate) || '—'}</td>
                                                    <td className="px-4 py-2 text-slate-700 dark:text-slate-300">{formatCurrency(acct.lastStmtAmount) || '—'}</td>
                                                    <td className="px-4 py-2 text-slate-700 dark:text-slate-300">{formatReadableDate(acct.lastPaymentDate) || '—'}</td>
                                                    <td className="px-4 py-2 text-slate-700 dark:text-slate-300">{formatCurrency(acct.lastPaymentAmount) || '—'}</td>
                                                    <td className="px-4 py-2">
                                                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 ring-1 ring-inset ring-red-200 dark:ring-red-800">
                                                            ⚠ OB account does not exist — create or remove from utility portal
                                                        </span>
                                                    </td>
                                                </tr>
                                            )
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </DisclosurePanel>
                    </>
                )}
            </Disclosure>
        </div>
    )
}

export default PortalOnlyAccountsTable
